// Cibles creuses (morph targets MakeHuman) : chargement paresseux par fichier + application CPU.
import { fetchBin, fetchJson, resolveUrl } from './fetcher';
import type { TargetIndex } from './format';

export interface SparseTarget {
  /** Indices de sommets de base. */
  idx: Uint16Array;
  /** Deltas quantifiés (x, y, z) ; delta réel = d * scale (mètres). */
  d: Int16Array;
  scale: number;
}

export class TargetStore {
  private readonly cache = new Map<string, SparseTarget>();

  private constructor(readonly baseUrl: string, readonly index: TargetIndex) {}

  static async load(baseUrl: string): Promise<TargetStore> {
    const index = await fetchJson<TargetIndex>(resolveUrl(baseUrl, 'targets/index.json'));
    return new TargetStore(baseUrl, index);
  }

  has(name: string) {
    return name in this.index.targets;
  }

  /** Télécharge les fichiers contenant les cibles demandées (cibles vides ou inconnues ignorées). */
  async ensure(names: Iterable<string>): Promise<void> {
    const list = [...names];
    const files = new Set<number>();
    for (const n of list) {
      const e = this.index.targets[n];
      if (e && e[2] > 0 && !this.cache.has(n)) files.add(e[0]);
    }
    await Promise.all([...files].map((f) => fetchBin(resolveUrl(this.baseUrl, 'targets/' + this.index.files[f]))));
    // Les buffers sont maintenant en cache : on crée les vues.
    await Promise.all(list.map((n) => this.get(n)));
  }

  /** Cible (vue sans copie sur le fichier), ou null si vide / inconnue. */
  async get(name: string): Promise<SparseTarget | null> {
    const hit = this.cache.get(name);
    if (hit) return hit;
    const e = this.index.targets[name];
    if (!e || e[2] === 0) return null;
    const [fi, off, n, scale] = e;
    const buf = await fetchBin(resolveUrl(this.baseUrl, 'targets/' + this.index.files[fi]));
    const dOff = off + ((n * 2 + 3) & ~3);
    const t: SparseTarget = { idx: new Uint16Array(buf, off, n), d: new Int16Array(buf, dOff, n * 3), scale };
    this.cache.set(name, t);
    return t;
  }

  /** Version synchrone (cible déjà chargée via ensure()). */
  peek(name: string): SparseTarget | null {
    return this.cache.get(name) ?? null;
  }
}

/** out += w * target (positions de base, xyz entrelacés). */
export function addTarget(out: Float32Array, t: SparseTarget, w: number) {
  const { idx, d } = t;
  const s = t.scale * w;
  for (let i = 0, n = idx.length; i < n; i++) {
    const o = idx[i] * 3, j = i * 3;
    out[o] += d[j] * s;
    out[o + 1] += d[j + 1] * s;
    out[o + 2] += d[j + 2] * s;
  }
}
